import React from 'react';

import { useSelector } from 'editor/src/store/hooks';

import spliceIntoTranslation, { TRANSLATION_SLOT } from 'editor/src/util/spliceIntoTranslation';
import useTr from 'editor/src/util/useTr';

import styles from './index.module.scss';

interface Props {
  name: string;
  spreadName: string;
}

/**
 * Names one layer of a shared-value group: **Text 4** on its own where the
 * design has a single page, "Front - **Text 4**" where it has more.
 *
 * One component for every place a member is named — the field's own panel and
 * the design-wide summary — because the two sit a scroll apart and a reader
 * matching one list against the other cannot afford them to be written
 * differently.
 *
 * The name carries the emphasis: it is what tells two members apart, and the
 * page is the shared prefix a reader skims past. The page is dropped entirely
 * on a single-page design, where it distinguishes nothing and is the same word
 * on every row.
 *
 * Assembled from a key rather than in JSX: the separator and the order of the
 * two halves are a translator's call, and CJK sets both differently.
 */
function SharedTextFieldLabel({ name, spreadName }: Props) {
  const tr = useTr();
  // Read here rather than passed in, so no caller can render the long form
  // while its neighbour renders the short one.
  const onePage = useSelector((state) => (state.design.designData?.spreads.length ?? 0) <= 1);

  const label = <span className={styles.name}>{name}</span>;

  if (onePage || !spreadName) {
    return label;
  }

  // A translation that lost the placeholder falls back to the name alone, which
  // is the right half to keep: it identifies the layer, and the page is only
  // ever context around it. See spliceIntoTranslation.
  return (
    <>
      {spliceIntoTranslation(
        tr('editor-shared-field-target', '{{spread}} - {{name}}', {
          spread: spreadName,
          name: TRANSLATION_SLOT,
        }),
        label,
      )}
    </>
  );
}

export default SharedTextFieldLabel;
